import axios from 'axios';
import api from './api';
import { isAxiosError } from 'axios';
import { logging } from './logging';
import type { OrderItemStatus } from '@/types/orderStatus';

interface UploadResponse {
  message: string;
  duplicate?: boolean;
  file: {
    id: string;
    originalWidthPx?: number;
    originalHeightPx?: number;
    dpiX?: number;
    dpiY?: number;
    widthIn?: number;
    heightIn?: number;
  };
  orderItem?: {
    id: string;
    uploadedFileId?: string | null;
    status: OrderItemStatus;
    orderId?: string | null;
    widthIn?: number | null;
    heightIn?: number | null;
    priceUsd?: number | null;
    createdAt: string;
    updatedAt: string;
  };
}

export interface UploadMetadata {
  readonly id: string;
  readonly url?: string;
  readonly width?: number;
  readonly height?: number;
  readonly dpiX?: number;
  readonly dpiY?: number;
  readonly widthIn?: number;
  readonly heightIn?: number;
}

interface DirectUploadInitResponse extends Partial<UploadResponse> {
  duplicate: boolean;
  uploadUrl?: string;
  objectName?: string;
  expiresInSeconds?: number;
  requiredHeaders?: Record<string, string>;
  reuploadOrderItemId?: string;
}

type UploadFileOptions = {
  readonly reuploadOrderItemId?: string;
};

const getFileMetadata = (file: File) => ({
  name: file.name,
  size: file.size,
  type: file.type || undefined,
  lastModified: file.lastModified,
});

const summarizeResponseData = (data: unknown): unknown => {
  if (typeof data === 'string') {
    return data.slice(0, 500);
  }
  if (data && typeof data === 'object') {
    return data;
  }
  return undefined;
};

const bytesToHex = (bytes: ArrayBuffer): string => {
  return Array.from(new Uint8Array(bytes))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
};

const readFileAsArrayBuffer = async (file: File): Promise<ArrayBuffer> => {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(reader.result);
        return;
      }
      reject(new Error('Could not read file as an ArrayBuffer.'));
    };
    reader.onerror = () => reject(reader.error ?? new Error('Could not read file.'));
    reader.readAsArrayBuffer(file);
  });
};

export const computeFileSha256 = async (file: File): Promise<string> => {
  const hashBuffer = await crypto.subtle.digest('SHA-256', await readFileAsArrayBuffer(file));
  return bytesToHex(hashBuffer);
};

export const getUploadContentType = (file: File): string => {
  return file.type || 'application/octet-stream';
};

export const uploadFileToSignedUrl = async (
  uploadUrl: string,
  file: File,
  headers: Record<string, string> = {},
): Promise<void> => {
  await axios.put(uploadUrl, file, {
    headers,
    withCredentials: false,
  });
};

const uploadViaDirectGCS = async (file: File, fileHash: string, options?: UploadFileOptions): Promise<UploadResponse> => {
  const contentType = getUploadContentType(file);
  const initResponse = await api.post<DirectUploadInitResponse>('/upload/direct/init', {
    filename: file.name,
    contentType,
    sizeBytes: file.size,
    fileHash,
    ...(options?.reuploadOrderItemId ? { reuploadOrderItemId: options.reuploadOrderItemId } : {}),
  });

  if (initResponse.data.duplicate) {
    return initResponse.data as UploadResponse;
  }

  const { uploadUrl, objectName } = initResponse.data;
  if (!uploadUrl || !objectName) {
    throw new Error('Direct upload initialization did not return an upload URL.');
  }

  await uploadFileToSignedUrl(
    uploadUrl,
    file,
    {
      ...(initResponse.data.requiredHeaders ?? {}),
      'Content-Type': contentType,
    },
  );

  const completeResponse = await api.post<UploadResponse>('/upload/direct/complete', {
    objectName,
    filename: file.name,
    contentType,
    sizeBytes: file.size,
    fileHash,
    ...(options?.reuploadOrderItemId ? { reuploadOrderItemId: options.reuploadOrderItemId } : {}),
  });

  return completeResponse.data
};

/**
 * Uploads a file to the server.
 * @param file The file to upload.
 * @returns A promise that resolves with the upload response data.
 */
export const uploadFile = async (file: File, options?: UploadFileOptions): Promise<UploadResponse> => {
  try {
    const fileHash = await computeFileSha256(file);
    if (!options?.reuploadOrderItemId) {
      const duplicateResponse = await api.post<DirectUploadInitResponse>('/upload/direct/check-duplicate', {
        fileHash,
      });

      if (duplicateResponse.data.duplicate) {
        return duplicateResponse.data as UploadResponse;
      }
    }

    return uploadViaDirectGCS(file, fileHash, options);
  } catch (error) {
    // The api instance's interceptor will handle token refresh.
    // Only log unexpected failures; duplicate uploads are expected (409).
    if (!isAxiosError(error) || error.response?.status !== 409) {
      logging.error('File upload failed', {
        error,
        file: getFileMetadata(file),
        uploadMode: 'direct-gcs',
        response: isAxiosError(error)
          ? {
              status: error.response?.status,
              statusText: error.response?.statusText,
              data: summarizeResponseData(error.response?.data),
              requestId: error.response?.headers?.['x-request-id'],
            }
          : undefined,
      });
    }
    throw error;
  }
};

export interface SignedUrlResponse {
  id: string;
  signedUrl: string;
  thumbnailSignedUrl?: string;
  mimeType?: string;
  expiresInSeconds: number;
}

export const getSignedUrlForFile = async (fileId: string): Promise<SignedUrlResponse> => {
  const { data } = await api.get<SignedUrlResponse>(`/upload/${fileId}/signed`);
  return data;
};

export const getUploadMetadata = async (fileId: string): Promise<UploadMetadata> => {
  const { data } = await api.get<UploadMetadata>(`/upload/${fileId}`);
  return data;
};
